import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getEventCount, getZoomRecordings, getActiveTeachers } from '../../../Api/apiUtils';
import { Video } from 'react-feather';
import { Page, PageHeader, Button, Table, Loader, EmptyState } from '../../../ui';
import './eventcount.scss';

interface ActiveTeacher {
  userId: number;
  teacherId: number;
  firstName: string;
  lastName: string;
}

interface EventItem {
  hasImage: boolean;
  event: {
    given_from?: string;
    utc_from?: string;
    imageUrl?: string;
    name?: string;
    given_to?: string;
    description?: string;
    type?: string;
    allowLinkShare?: boolean;
    utc_to?: string;
    joinUrl?: string;
    meetingid?: number;
    startUrl?: string;
  };
  messageId: string;
  hasVideo: boolean;
  senderId: string;
  hasDocument: boolean;
  timestamp: string;
  communityId: string;
  text: string;
  hasLink: boolean;
  hasAudio: boolean;
}

interface EventCountResponse {
  success: boolean;
  pagesScanned: number;
  totalEvents: number;
  scannedCount: number;
  lastEvaluatedKey: any;
  events: EventItem[];
}

const EventCount: React.FC = () => {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<any>(null);
  const [pageSize] = useState(20);
  const [teacherMap, setTeacherMap] = useState<Record<string, { firstName: string; lastName: string; userId: number; teacherId: number }>>({});
  const [showUserIdColumn, setShowUserIdColumn] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const lastEvaluatedKeyRef = useRef<any>(null);

  // Update ref whenever lastEvaluatedKey changes
  useEffect(() => {
    lastEvaluatedKeyRef.current = lastEvaluatedKey;
  }, [lastEvaluatedKey]);

  // Fetch active teachers. Only when API returns full [] do we show Teacher User Id column.
  useEffect(() => {
    const loadActiveTeachers = async () => {
      try {
        const teachers = await getActiveTeachers() as ActiveTeacher[] | null | undefined;
        if (teachers == null || !Array.isArray(teachers)) {
          setShowUserIdColumn(false);
          setTeacherMap({});
          return;
        }
        setShowUserIdColumn(teachers.length === 0);
        const map: Record<string, { firstName: string; lastName: string; userId: number; teacherId: number }> = {};
        teachers.forEach((t) => {
          if (t?.userId != null && t?.teacherId != null) {
            map[String(t.userId)] = {
              firstName: t.firstName ?? '',
              lastName: t.lastName ?? '',
              userId: t.userId,
              teacherId: t.teacherId
            };
          }
        });
        setTeacherMap(map);
      } catch (error) {
        console.error('Error loading active teachers:', error);
        setShowUserIdColumn(false);
      }
    };
    loadActiveTeachers();
  }, []);

  const fetchData = useCallback(async (isPagination: boolean = false) => {
    if (isPagination) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    
    try {
      const lastKey = isPagination && lastEvaluatedKeyRef.current ? JSON.stringify(lastEvaluatedKeyRef.current) : undefined;
      const response = await getEventCount(pageSize, lastKey);
      
      if (response?.success && response?.events) {
        if (isPagination) {
          setEvents((prev) => [...prev, ...response.events]);
        } else {
          setEvents(response.events);
        }
        setLastEvaluatedKey(response.lastEvaluatedKey);
      } else {
        if (!isPagination) {
          setEvents([]);
          toast.info('No events found');
        }
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to fetch events');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [pageSize]);

  useEffect(() => {
    fetchData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!tableContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;
    const isNearBottom = scrollHeight - scrollTop <= clientHeight * 1.1;
    
    if (isNearBottom && !loading && !loadingMore && lastEvaluatedKeyRef.current) {
      fetchData(true);
    }
  }, [loading, loadingMore, fetchData]);

  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener('scroll', handleScroll);
      return () => tableContainer.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const formatLocalTime = (utcTime: string | undefined): string => {
    if (!utcTime) return '-';
    try {
      const date = new Date(utcTime);
      return date.toLocaleString();
    } catch (error) {
      return utcTime;
    }
  };

  const handleZoomRecording = async (messageId: string) => {
    try {
      const response = await getZoomRecordings(messageId);
      
      if (response && Array.isArray(response) && response.length > 0) {
        const playUrl = response[0]?.playUrl;
        
        if (playUrl) {
          await navigator.clipboard.writeText(playUrl);
          toast.success('Recording copied to clipboard');
        } else {
          toast.error('Play URL not found in recording');
        }
      } else {
        toast.info('No recording found for this event');
      }
    } catch (error) {
      console.error('Error fetching zoom recording:', error);
      toast.error('Failed to fetch recording');
    }
  };

  // Filter events to only show those with both utc_from and utc_to
  const filteredEvents = events.filter(
    (event) => event.event?.utc_from && event.event?.utc_to
  );

  const basePath = process.env.PUBLIC_URL || '';
  const teacherProfilePath = (userId: number, teacherId: number) => `${basePath}/pages/teacher-profile/${userId}/${teacherId}`;
  const profilePath = (senderId: string) => `${basePath}/profile/${senderId}`;

  return (
    <Page>
      <PageHeader
        eyebrow="Community"
        title="Community Events"
        subtitle={loading ? 'Loading events…' : `${filteredEvents.length} scheduled events`}
      />

      {loading ? (
        <Loader label="Loading events…" />
      ) : filteredEvents.length > 0 ? (
        <>
          <Table caption="Community events" stickyHeader wrapRef={tableContainerRef} wrapClassName="event-count-scroll">
            <thead>
              <tr>
                {!showUserIdColumn && <th>Teacher</th>}
                {showUserIdColumn && <th>Teacher user ID</th>}
                <th>From (IST)</th>
                <th>To (IST)</th>
                <th>Join URL</th>
                <th>Zoom recording</th>
              </tr>
            </thead>
            <tbody>
              {filteredEvents.map((event, index) => {
                const teacher = teacherMap[event.senderId];
                const nameText = teacher
                  ? [teacher.firstName?.trim(), teacher.lastName?.trim()].filter(Boolean).join(' ') || '-'
                  : '-';
                const nameLink = teacher ? teacherProfilePath(teacher.userId, teacher.teacherId) : null;
                return (
                  <tr key={`${event.messageId}-${index}`}>
                    {!showUserIdColumn && (
                      <td>
                        {nameLink ? (
                          <Link className="ds-link" to={nameLink} target="_blank">{nameText}</Link>
                        ) : (
                          nameText
                        )}
                      </td>
                    )}
                    {showUserIdColumn && (
                      <td>
                        <Link className="ds-link" target="_blank" to={profilePath(event.senderId)}>{event.senderId}</Link>
                      </td>
                    )}
                    <td className="ds-num">{formatLocalTime(event.event.utc_from)}</td>
                    <td className="ds-num">{formatLocalTime(event.event.utc_to)}</td>
                    <td className="event-count-url">
                      {event.event.joinUrl ? (
                        <a className="ds-link" href={event.event.joinUrl} target="_blank" rel="noopener noreferrer">
                          {event.event.joinUrl}
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      {event.event.joinUrl ? (
                        <Button size="sm" variant="secondary" icon={<Video size={14} aria-hidden />} onClick={() => handleZoomRecording(event.messageId)}>
                          Recording
                        </Button>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          {loadingMore && <Loader label="Loading more events…" />}
        </>
      ) : (
        <EmptyState title="No events found" description="Scheduled community events will show up here." />
      )}
    </Page>
  );
};

export default EventCount;